import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { documenti as documentiApi } from '../../utils/api';
import SkeletonLoader from '../../components/shared/SkeletonLoader';
import FeatureGate from '../../components/shared/FeatureGate';  // CAP-008
import Toast from '../../components/shared/Toast';
import { useToast } from '../../hooks/useToast';
import UploadDocumentoModal from '../../components/documenti/UploadDocumentoModal';
import SidebarCartelle from '../../components/documenti/SidebarCartelle';
import FiltriDocumenti from '../../components/documenti/FiltriDocumenti';
import MiniNav from '../../components/documenti/MiniNav';
import VistaGriglia from '../../components/documenti/VistaGriglia';
import PannelloDettaglio from '../../components/documenti/PannelloDettaglio';
import Storico from './Storico';
import useArchivioBlocchi from './useArchivioBlocchi';
import { statoPreferito } from './archivioBlocchi';
import CaricaAltri from '../../components/documenti/CaricaAltri';
import KnowledgeBase from './KnowledgeBase';
import { contestoDaCartella } from '../../components/documenti/cartelleUpload';
import { FileText, FolderOpen, Calendar, Database, ChevronLeft, ChevronRight, Lock } from 'lucide-react';

const CARD_SHADOW = '0 6px 18px rgba(35,38,47,0.08)';
const tint = (c, pct) => `color-mix(in srgb, ${c} ${pct}%, transparent)`;

// Vista per periodo helpers
function groupByMonth(docs) {
  var groups = {};
  docs.forEach(function (doc) {
    var dateStr = doc.data_documento || doc.data_upload || doc.created_at;
    if (!dateStr) { dateStr = '1970-01-01'; }
    var d = new Date(dateStr);
    var key = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0');
    if (!groups[key]) groups[key] = [];
    groups[key].push(doc);
  });
  return Object.keys(groups)
    .sort(function (a, b) { return b.localeCompare(a); })
    .map(function (key) {
      var parts = key.split('-');
      var mesi = ['Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno','Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'];
      return {
        key: key,
        label: mesi[parseInt(parts[1], 10) - 1] + ' ' + parts[0],
        docs: groups[key],
      };
    });
}

var TIPO_COLORS = {
  contratto: 'var(--zone-blue)', preventivo: 'var(--zone-amber)', fattura: 'var(--ok)',
  ddt: 'var(--zone-violet)', bolla: 'var(--zone-blue)', foto: 'var(--zone-magenta)',
  pdf_generico: 'var(--ds-danger)', certificato: 'var(--ok)', altro: 'var(--ds-muted)',
};

var TIPO_LABEL = {
  preventivo: 'Preventivo', contratto: 'Contratto', fattura: 'Fattura',
  foto: 'Foto', ddt: 'DDT', bolla: 'Bolla', certificato: 'Certificato',
  collaudo: 'Collaudo', planimetria: 'Planimetria', sal: 'SAL',
  computo: 'Computo metrico', corrispondenza: 'Corrispondenza',
  pdf_generico: 'PDF generico', altro: 'Altro',
};

function readSidebarState() {
  try {
    var val = localStorage.getItem('sidebar_documenti_open');
    if (val === null) return true;
    return val === 'true';
  } catch (e) { return true; }
}

export default function Documenti() {
  var { hasFeature } = useAuth();
  var archivioEnabled = hasFeature('archivio_cartelle');

  // Vista attiva dalla MiniNav
  var [vistaAttiva, setVistaAttiva] = useState('archivio');

  // Stats
  var [stats, setStats]         = useState(null);
  var [statsLoading, setStatsLoading] = useState(true);
  var [showUpload, setShowUpload]     = useState(false);
  var [refreshKey, setRefreshKey]     = useState(0);
  var { toasts, showToast, dismissToast } = useToast();
  var location = useLocation();

  // Apri modal upload automaticamente se navigazione da Home/sidebar (+Nuovo documento)
  useEffect(function () {
    if (location.pathname === '/documenti/nuovo') { setShowUpload(true); }
  }, []); // eslint-disable-line

  // Sidebar cartelle state
  var [cartelle, setCartelle]               = useState([]);
  var [cartelleLoading, setCartelleLoading] = useState(false);
  var [selectedCartella, setSelectedCartella] = useState(null);
  var [sidebarOpen, setSidebarOpen] = useState(readSidebarState);

  // Filtri state (vista Lista)
  var [filtri, setFiltri] = useState({
    tipo: '', provenienza: '', tag: '',
    data_documento_da: '', data_documento_a: '',
  });

  // Vista per periodo toggle (vista Lista)
  var [vistaPerPeriodo, setVistaPerPeriodo] = useState(false);

  // Pannello dettaglio + preferiti
  var [selectedDoc, setSelectedDoc] = useState(null);
  // FE-084 — solo le scelte fatte in questa pagina (id → vero/falso). Lo stato di
  // partenza di ogni stellina e' il campo `preferito` del documento, calcolato
  // dal server: prima si caricava a parte l'elenco dei preferiti, fermo al primo
  // blocco da 20, e dal 21° in poi la stellina era vuota.
  var [preferitiLocali, setPreferitiLocali] = useState({});

  // Cartella attiva per VistaGriglia archivio
  var [cartellaGriglia, setCartellaGriglia] = useState(null);

  var loadStats = async function () {
    setStatsLoading(true);
    try {
      var res = await documentiApi.statistiche();
      setStats(res.data);
    } catch (e) {
      console.warn('Errore caricamento statistiche:', e);
    } finally { setStatsLoading(false); }
  };

  var loadCartelle = async function () {
    if (!archivioEnabled) return;
    setCartelleLoading(true);
    try {
      var res = await documentiApi.getCartelle();
      setCartelle(res.data.results || res.data || []);
    } catch (e) {
      console.warn('Errore caricamento cartelle:', e);
    } finally { setCartelleLoading(false); }
  };

  // Lista «per periodo»: a blocchi come le altre viste (FE-084).
  var paramsPeriodo = {};
  if (selectedCartella) paramsPeriodo.cartella = selectedCartella;
  if (filtri.tipo) paramsPeriodo.tipo = filtri.tipo;
  if (filtri.provenienza) paramsPeriodo.provenienza = filtri.provenienza;
  if (filtri.tag) paramsPeriodo.tag = filtri.tag;
  if (filtri.data_documento_da) paramsPeriodo.data_documento_da = filtri.data_documento_da;
  if (filtri.data_documento_a) paramsPeriodo.data_documento_a = filtri.data_documento_a;
  var elencoPeriodo = useArchivioBlocchi(paramsPeriodo, {
    attivo: vistaAttiva === 'lista' && vistaPerPeriodo,
    versione: refreshKey,
  });
  var docsFiltrati = elencoPeriodo.docs;

  useEffect(function () { loadStats(); loadCartelle(); }, [refreshKey]);

  var onSuccess = function () {
    setRefreshKey(function (k) { return k + 1; });
    showToast('Documento caricato', 'success');
  };

  var handleCreaCartella = async function (data) {
    await documentiApi.creaCartella(data);
    loadCartelle();
    showToast('Cartella creata', 'success');
  };

  var handleDeleteCartella = async function (id) {
    try {
      await documentiApi.eliminaCartella(id);
      if (selectedCartella === id) setSelectedCartella(null);
      loadCartelle();
      showToast('Cartella eliminata', 'success');
    } catch (e) {
      showToast('Errore eliminazione cartella', 'error');
    }
  };

  var handleFiltriChange = function (patch) {
    setFiltri(function (prev) { return Object.assign({}, prev, patch); });
  };

  var handleToggleSidebar = function () {
    setSidebarOpen(function (prev) {
      var next = !prev;
      try { localStorage.setItem('sidebar_documenti_open', String(next)); } catch (e) { /* silenzioso */ }
      return next;
    });
  };

  // `statoAttuale` e' quello che l'artigiano vede sulla stellina quando la preme.
  var impostaPreferito = function (docId, valore) {
    setPreferitiLocali(function (prev) {
      var next = Object.assign({}, prev);
      next[docId] = valore;
      return next;
    });
  };

  var handleTogglePreferito = async function (docId, statoAttuale) {
    impostaPreferito(docId, !statoAttuale);  // ottimistico
    try {
      var res = await documentiApi.togglePreferito(docId);
      // Il server dice com'e' finita: vale la sua risposta.
      if (res && res.data && typeof res.data.preferito === 'boolean') impostaPreferito(docId, res.data.preferito);
    } catch (e) {
      impostaPreferito(docId, statoAttuale);
      showToast('Non è stato possibile aggiornare il preferito', 'error');
    }
  };

  var handleSelectDoc = function (doc) {
    setSelectedDoc(doc);
  };

  var handleDeleteDoc = async function (docId) {
    try {
      await documentiApi.delete(docId);
      setRefreshKey(function (k) { return k + 1; });
      if (selectedDoc && selectedDoc.id === docId) setSelectedDoc(null);
      showToast('Documento eliminato', 'success');
    } catch (e) {
      showToast('Errore eliminazione documento', 'error');
    }
  };

  var handleColorChange = async function (cartellaId, colore) {
    try {
      await documentiApi.patchCartella(cartellaId, { colore: colore });
      loadCartelle();
    } catch (e) {
      showToast('Errore cambio colore', 'error');
    }
  };

  var handleCambiaVista = function (vista) {
    setVistaAttiva(vista);
    setSelectedDoc(null);
    if (vista !== 'archivio') {
      setCartellaGriglia(null);
    }
  };

  var totaleStorage = '0 KB';
  if (stats && stats.totale_bytes > 0) {
    if (stats.totale_bytes < 1048576) { totaleStorage = Math.round(stats.totale_bytes / 1024) + ' KB'; }
    else if (stats.totale_bytes < 1073741824) { totaleStorage = (stats.totale_bytes / 1048576).toFixed(1) + ' MB'; }
    else { totaleStorage = (stats.totale_bytes / 1073741824).toFixed(2) + ' GB'; }
  }

  var toggleStyle = function (active) {
    return {
      padding: '5px 12px', borderRadius: 'var(--radius-base)', border: 'none',
      cursor: 'pointer', fontSize: 11, fontWeight: 500,
      background: active ? 'var(--ds-bg)' : 'transparent',
      color: active ? 'var(--ink)' : 'var(--ds-muted)',
      boxShadow: active ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
      transition: 'all 0.15s',
    };
  };

  // Vista per periodo grouping
  var periodoGroups = vistaPerPeriodo ? groupByMonth(docsFiltrati) : [];

  // Flags per visibilita'
  var showSidebar = vistaAttiva === 'lista' && archivioEnabled;
  var showUpgradePanel = vistaAttiva === 'lista' && !archivioEnabled;
  var showVistaGriglia = vistaAttiva === 'archivio' || vistaAttiva === 'recenti' || vistaAttiva === 'preferiti' || vistaAttiva === 'condivisi';
  var showLista = vistaAttiva === 'lista';
  // DBT-156 — il caricamento parte dalla cartella APERTA (griglia o lista) e dal
  // cliente/cantiere/fornitore a cui appartiene: prima il modal partiva vuoto.
  var contestoUpload = contestoDaCartella(
    archivioEnabled ? cartelle : [],
    showVistaGriglia ? cartellaGriglia : selectedCartella,
  );
  var showKb = vistaAttiva === 'kb';
  var showPannello = selectedDoc !== null && vistaAttiva !== 'kb';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', boxSizing: 'border-box', background: 'var(--chip)' }}>
      <Toast toasts={toasts} dismissToast={dismissToast} />

      {/* Header */}
      <div style={{ padding: '20px 24px 0 24px', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
              <FileText size={22} color="var(--brand)" strokeWidth={2} />
              <h1 style={{ margin: 0, fontSize: 22, fontWeight: 500, color: 'var(--ink)', fontFamily: 'var(--font-display)' }}>Documenti</h1>
            </div>
            {statsLoading
              ? <SkeletonLoader type="page" count={1} />
              : <p style={{ margin: 0, fontSize: 12, color: 'var(--ds-muted)' }}>
                  {stats ? stats.totale : 0} documenti
                  {' \u00B7 '}
                  {totaleStorage} utilizzati
                </p>
            }
          </div>
          {vistaAttiva !== 'kb' && (
            <FeatureGate flag="storage_documenti" upsellText="Caricamento documenti: piano superiore">
              <button
                onClick={function () { setShowUpload(true); }}
                style={{ padding: '9px 20px', borderRadius: 'var(--radius-base)', border: 'none', background: 'var(--brand)', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}
              >
                + Carica documento
              </button>
            </FeatureGate>
          )}
        </div>
      </div>

      {/* MiniNav */}
      <MiniNav vistaAttiva={vistaAttiva} onCambiaVista={handleCambiaVista} />

      {/* Corpo: sidebar (opzionale) + contenuto + pannello dettaglio (opzionale) */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

        {/* Sidebar cartelle — solo vista Lista con archivio abilitato */}
        {showSidebar && (
          <div style={{ display: 'flex', alignItems: 'stretch', flexShrink: 0 }}>
            {sidebarOpen && (
              <SidebarCartelle
                cartelle={cartelle}
                loading={cartelleLoading}
                selectedCartella={selectedCartella}
                onSelectCartella={setSelectedCartella}
                onDeleteCartella={handleDeleteCartella}
                onCreaCartella={handleCreaCartella}
              />
            )}
            <div style={{ display: 'flex', alignItems: 'center', padding: '0 4px' }}>
              <button
                onClick={handleToggleSidebar}
                onMouseEnter={function(e) { e.currentTarget.style.background = 'rgba(46,160,132,0.2)'; }}
                onMouseLeave={function(e) { e.currentTarget.style.background = 'rgba(46,160,132,0.1)'; }}
                style={{
                  width: '44px', height: '44px', borderRadius: '50%',
                  border: '0.5px solid rgba(46,160,132,0.4)',
                  background: 'rgba(46,160,132,0.1)',
                  cursor: 'pointer', display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                  padding: 0, color: 'var(--brand)', flexShrink: 0,
                }}
                title={sidebarOpen ? 'Chiudi sidebar' : 'Apri sidebar'}
              >
                {sidebarOpen ? <ChevronLeft size={14} strokeWidth={2} /> : <ChevronRight size={14} strokeWidth={2} />}
              </button>
            </div>
          </div>
        )}

        {/* Pannello upgrade (vista Lista senza archivio) */}
        {showUpgradePanel && (
          <div style={{
            width: 240, minWidth: 240, borderRight: '1px solid var(--ds-border)',
            background: 'var(--ds-bg)', display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', padding: 20, textAlign: 'center',
          }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>
              <Lock size={32} color="var(--ds-muted)" strokeWidth={1.5} />
            </div>
            <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--ink)', marginBottom: 6 }}>Archivio Cartelle</div>
            <div style={{ fontSize: 12, color: 'var(--ds-muted)', marginBottom: 16, lineHeight: 1.5 }}>
              Organizza i documenti in cartelle con il piano Smart o superiore.
            </div>
            <button
              onClick={function () { window.location.href = '/impostazioni'; }}
              style={{
                padding: '8px 16px', borderRadius: 'var(--radius-base)', border: 'none',
                background: 'var(--brand)',
                color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 500,
              }}
            >
              Upgrade piano
            </button>
          </div>
        )}

        {/* Contenuto principale */}
        <div style={{ flex: 1, overflowY: 'auto', minWidth: 0 }}>

          {/* Vista Griglia (archivio / recenti / preferiti / condivisi) */}
          {showVistaGriglia && (
            <VistaGriglia
              refreshKey={refreshKey}
              vista={vistaAttiva}
              cartelle={cartelle}
              cartellaAttiva={cartellaGriglia}
              onNavigaCartella={setCartellaGriglia}
              onSelectDoc={handleSelectDoc}
              selectedDocId={selectedDoc ? selectedDoc.id : null}
              onDeleteDoc={handleDeleteDoc}
              onDeleteCartella={handleDeleteCartella}
              onColorChange={handleColorChange}
              onUpload={function() { setShowUpload(true); }}
              onTogglePreferito={handleTogglePreferito}
              preferitiLocali={preferitiLocali}
            />
          )}

          {/* Vista Lista */}
          {showLista && (
            <div style={{ padding: '16px 20px 28px 24px' }}>
              {/* KPI cards */}
              {stats && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 20 }}>
                  {[
                    { label: 'Totale documenti', value: stats.totale || 0, icon: FolderOpen },
                    { label: 'Ultimo caricamento', value: stats.ultimo_upload ? new Date(stats.ultimo_upload).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' }) : 'Mai', icon: Calendar },
                    { label: 'Spazio usato', value: totaleStorage, icon: Database },
                  ].map(function (kpi) {
                    var IconComp = kpi.icon;
                    return (
                      <div key={kpi.label} style={{ background: 'var(--ds-bg)', borderRadius: 'var(--radius-card)', padding: '12px 16px', border: '1px solid var(--ds-border)', boxShadow: CARD_SHADOW }}>
                        <div style={{ marginBottom: 4 }}><IconComp size={18} color="var(--brand)" strokeWidth={2} /></div>
                        <div style={{ fontSize: 18, fontWeight: 500, color: 'var(--ink)', fontFamily: 'var(--font-display)' }}>{kpi.value}</div>
                        <div style={{ fontSize: 12, color: 'var(--ds-muted)', marginTop: 2 }}>{kpi.label}</div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Toggle vista Lista/Per periodo + Filtri */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
                <FiltriDocumenti filtri={filtri} onChange={handleFiltriChange} hasArchivio={archivioEnabled} />
                <div style={{ display: 'flex', gap: 2, background: 'var(--chip)', borderRadius: 'var(--radius-base)', padding: 3 }}>
                  <button style={toggleStyle(!vistaPerPeriodo)} onClick={function () { setVistaPerPeriodo(false); }}>Lista</button>
                  <button style={toggleStyle(vistaPerPeriodo)} onClick={function () { setVistaPerPeriodo(true); }}>Per periodo</button>
                </div>
              </div>

              {/* Contenuto lista */}
              {vistaPerPeriodo ? (
                elencoPeriodo.loading ? (
                  <SkeletonLoader type="list" count={5} />
                ) : elencoPeriodo.error ? (
                  <div style={{ padding: 20, color: 'var(--ds-danger)', fontSize: 13 }}>
                    {elencoPeriodo.error}{' '}
                    <button onClick={elencoPeriodo.ricarica} style={{ marginLeft: 8, color: 'var(--brand)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 500, fontSize: 13 }}>Riprova</button>
                  </div>
                ) : docsFiltrati.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '48px 20px' }}>
                    <div style={{ fontSize: 48, marginBottom: 12 }}>
                      <FolderOpen size={48} color="var(--ds-muted)" strokeWidth={1.5} />
                    </div>
                    <div style={{ fontWeight: 500, fontSize: 15, color: 'var(--ink)', marginBottom: 6 }}>Nessun documento</div>
                    <div style={{ fontSize: 12, color: 'var(--ds-muted)' }}>Prova a modificare i filtri.</div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                    {periodoGroups.map(function (group) {
                      return (
                        <div key={group.key}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                            <h3 style={{ margin: 0, fontSize: 14, fontWeight: 500, color: 'var(--ink)', fontFamily: 'var(--font-display)' }}>{group.label}</h3>
                            <span style={{ fontSize: 11, color: 'var(--ds-muted)', fontWeight: 500 }}>
                              {' \u2014 ' + group.docs.length + ' document' + (group.docs.length === 1 ? 'o' : 'i')}
                            </span>
                          </div>
                          <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
                            {Object.entries(
                              group.docs.reduce(function (acc, d) { acc[d.tipo] = (acc[d.tipo] || 0) + 1; return acc; }, {})
                            ).map(function (entry) {
                              var tipo = entry[0];
                              var count = entry[1];
                              var color = TIPO_COLORS[tipo] || 'var(--ds-muted)';
                              return (
                                <span key={tipo} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 500, color: color, background: tint(color, 14), padding: '2px 8px', borderRadius: 99 }}>
                                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: color }} />
                                  {count}
                                </span>
                              );
                            })}
                          </div>
                          <div style={{ background: 'var(--ds-bg)', borderRadius: 'var(--radius-card)', border: '1px solid var(--ds-border)', overflow: 'hidden', boxShadow: CARD_SHADOW }}>
                            {group.docs.map(function (doc, idx) {
                              var tipoColor = TIPO_COLORS[doc.tipo] || 'var(--ds-muted)';
                              return (
                                <div
                                  key={doc.id}
                                  style={{
                                    display: 'flex', alignItems: 'center', gap: 12,
                                    padding: '10px 14px', cursor: 'pointer',
                                    borderBottom: idx < group.docs.length - 1 ? '1px solid var(--ds-border)' : 'none',
                                    background: selectedDoc && selectedDoc.id === doc.id ? tint('var(--brand)', 8) : 'transparent',
                                  }}
                                  onClick={function () { handleSelectDoc(doc); }}
                                >
                                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: tipoColor, flexShrink: 0 }} />
                                  <span style={{ flex: 1, fontSize: 13, fontWeight: 500, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {doc.nome_file}
                                  </span>
                                  <span style={{ fontSize: 10, color: tipoColor, fontWeight: 500, background: tint(tipoColor, 14), padding: '2px 8px', borderRadius: 99 }}>
                                    {TIPO_LABEL[doc.tipo] || doc.tipo}
                                  </span>
                                  <span style={{ fontSize: 11, color: 'var(--ds-muted)', whiteSpace: 'nowrap' }}>
                                    {/* FE-075 — fattura/preventivo senza file salvato: il PDF si genera
                                        al volo, quindi mostra "PDF" invece di "0 KB" (mai un dato falso). */}
                                    {doc.dimensione_bytes ? doc.dimensione_leggibile : (doc.scaricabile ? 'PDF' : '')}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                    <CaricaAltri elenco={elencoPeriodo} />
                  </div>
                )
              ) : (
                <Storico
                  key={refreshKey}
                  onUpload={function () { setShowUpload(true); }}
                  showToast={showToast}
                  extraParams={{
                    cartella: selectedCartella || undefined,
                    provenienza: filtri.provenienza || undefined,
                    tag: filtri.tag || undefined,
                    data_documento_da: filtri.data_documento_da || undefined,
                    data_documento_a: filtri.data_documento_a || undefined,
                    tipo: filtri.tipo || undefined,
                  }}
                  onTogglePreferito={handleTogglePreferito}
                  preferitiLocali={preferitiLocali}
                />
              )}
            </div>
          )}

          {/* Knowledge Base */}
          {showKb && (
            <div style={{ padding: '16px 20px 28px 24px' }}>
              <KnowledgeBase showToast={showToast} />
            </div>
          )}
        </div>

        {/* Pannello dettaglio */}
        {showPannello && (
          <PannelloDettaglio
            documento={selectedDoc}
            onChiudi={function () { setSelectedDoc(null); }}
            onTogglePreferito={handleTogglePreferito}
            isPreferito={statoPreferito(selectedDoc, preferitiLocali)}
          />
        )}
      </div>

      {/* Upload modal */}
      {showUpload && (
        <UploadDocumentoModal
          onClose={function () { setShowUpload(false); }}
          onSuccess={onSuccess}
          cartelle={archivioEnabled ? cartelle : []}
          hasArchivio={archivioEnabled}
          defaultCartella={contestoUpload.cartella}
          defaultCliente={contestoUpload.cliente}
          defaultCantiere={contestoUpload.cantiere}
          defaultFornitore={contestoUpload.fornitore}
        />
      )}
    </div>
  );
}
